import { ApiResponse, Plan, PlanDetailsResponse, PlanItem, PlanItemStatus, PlanItemType, PlanPeriod, PlanReview, PlanStatus, Project, ProjectFormData, ShoppingItem, ShoppingOffer, ShoppingScope, ShoppingStatus } from '../types';
import api from './apiClient';

export const projectsAPI = {
  getProjects: async (): Promise<ApiResponse<{ projects: Project[] }>> => {
    const response = await api.get('/projects');
    return response.data;
  },

  getProject: async (id: number): Promise<ApiResponse<{ project: Project }>> => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },

  createProject: async (data: ProjectFormData): Promise<ApiResponse<{ project: Project }>> => {
    const response = await api.post('/projects', data);
    return response.data;
  },

  updateProject: async (id: number, data: Partial<ProjectFormData>): Promise<ApiResponse<{ project: Project }>> => {
    const response = await api.put(`/projects/${id}`, data);
    return response.data;
  },

  deleteProject: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/projects/${id}`);
    return response.data;
  },

  addProjectComment: async (id: number, comment: string): Promise<ApiResponse<{ comment: any }>> => {
    const response = await api.post(`/projects/${id}/comments`, { comment });
    return response.data;
  },

  getAvailableColors: async (): Promise<ApiResponse<{ colors: Array<{ hex: string; name: string }> }>> => {
    const response = await api.get('/projects/colors/available');
    return response.data;
  },
};


export const plansAPI = {
  getPlans: async (params?: { period?: PlanPeriod; status?: PlanStatus }): Promise<ApiResponse<{ plans: Plan[] }>> => {
    const response = await api.get('/plans', { params });
    return response.data;
  },

  getPlan: async (id: number): Promise<ApiResponse<PlanDetailsResponse>> => {
    const response = await api.get(`/plans/${id}`);
    return response.data;
  },

  createPlan: async (data: {
    period: PlanPeriod;
    title: string;
    description?: string;
    status?: PlanStatus;
    startDate: string;
    endDate: string;
    vision?: string;
    successMetric?: string;
  }): Promise<ApiResponse<PlanDetailsResponse>> => {
    const response = await api.post('/plans', data);
    return response.data;
  },

  updatePlan: async (id: number, data: Partial<{
    period: PlanPeriod;
    title: string;
    description?: string;
    status?: PlanStatus;
    startDate: string;
    endDate: string;
    vision?: string;
    successMetric?: string;
  }>): Promise<ApiResponse<PlanDetailsResponse>> => {
    const response = await api.put(`/plans/${id}`, data);
    return response.data;
  },

  createItem: async (planId: number, data: {
    parentItemId?: number | null;
    itemType?: PlanItemType;
    title: string;
    description?: string;
    status?: PlanItemStatus;
    priority?: 1 | 2 | 3;
    progress?: number;
    targetValue?: number | null;
    currentValue?: number | null;
    unit?: string | null;
    dueDate?: string | null;
    linkedTaskId?: number | null;
    linkedProjectId?: number | null;
    sortOrder?: number;
    carryForward?: boolean;
  }): Promise<ApiResponse<{ item: PlanItem }>> => {
    const response = await api.post(`/plans/${planId}/items`, data);
    return response.data;
  },

  updateItem: async (planId: number, itemId: number, data: Partial<{
    parentItemId?: number | null;
    itemType?: PlanItemType;
    title: string;
    description?: string;
    status?: PlanItemStatus;
    priority?: 1 | 2 | 3;
    progress?: number;
    targetValue?: number | null;
    currentValue?: number | null;
    unit?: string | null;
    dueDate?: string | null;
    linkedTaskId?: number | null;
    linkedProjectId?: number | null;
    sortOrder?: number;
    carryForward?: boolean;
  }>): Promise<ApiResponse<{ item: PlanItem }>> => {
    const response = await api.put(`/plans/${planId}/items/${itemId}`, data);
    return response.data;
  },

  deleteItem: async (planId: number, itemId: number): Promise<ApiResponse> => {
    const response = await api.delete(`/plans/${planId}/items/${itemId}`);
    return response.data;
  },

  createReview: async (planId: number, data: {
    summary?: string;
    wins?: string;
    blockers?: string;
    carryForwardNotes?: string;
    markCompleted?: boolean;
  }): Promise<ApiResponse<{ review: PlanReview }>> => {
    const response = await api.post(`/plans/${planId}/review`, data);
    return response.data;
  },

  carryForward: async (planId: number): Promise<ApiResponse<PlanDetailsResponse & { carriedItemsCount: number }>> => {
    const response = await api.post(`/plans/${planId}/carry-forward`);
    return response.data;
  }
};

export const shoppingAPI = {
  getItems: async (params?: {
    scope?: ShoppingScope;
    status?: ShoppingStatus;
    search?: string;
  }): Promise<ApiResponse<{ items: ShoppingItem[] }> & { items: ShoppingItem[] }> => {
    const response = await api.get('/shopping', { params });
    return response.data;
  },

  getItem: async (id: number): Promise<ApiResponse<{ item: ShoppingItem; offers: ShoppingOffer[] }> & { item: ShoppingItem; offers: ShoppingOffer[] }> => {
    const response = await api.get(`/shopping/${id}`);
    return response.data;
  },

  createItem: async (data: Partial<ShoppingItem> & { scope: ShoppingScope; title: string }): Promise<ApiResponse<{ item: ShoppingItem }> & { item: ShoppingItem }> => {
    const response = await api.post('/shopping', data);
    return response.data;
  },

  updateItem: async (id: number, data: Partial<ShoppingItem>): Promise<ApiResponse<{ item: ShoppingItem }> & { item: ShoppingItem }> => {
    const response = await api.put(`/shopping/${id}`, data);
    return response.data;
  },

  deleteItem: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/shopping/${id}`);
    return response.data;
  },

  createOffer: async (itemId: number, data: Partial<ShoppingOffer> & { vendor: string; productName: string }): Promise<ApiResponse<{ offer: ShoppingOffer }> & { offer: ShoppingOffer }> => {
    const response = await api.post(`/shopping/${itemId}/offers`, data);
    return response.data;
  },

  updateOffer: async (itemId: number, offerId: number, data: Partial<ShoppingOffer>): Promise<ApiResponse<{ offer: ShoppingOffer }> & { offer: ShoppingOffer }> => {
    const response = await api.put(`/shopping/${itemId}/offers/${offerId}`, data);
    return response.data;
  },

  deleteOffer: async (itemId: number, offerId: number): Promise<ApiResponse> => {
    const response = await api.delete(`/shopping/${itemId}/offers/${offerId}`);
    return response.data;
  },

  selectOffer: async (itemId: number, offerId?: number | null): Promise<ApiResponse<{ item: ShoppingItem }> & { item: ShoppingItem }> => {
    const response = await api.post(`/shopping/${itemId}/select-offer`, { offerId: offerId ?? null });
    return response.data;
  },

  createPayable: async (itemId: number, data?: {
    amount?: number;
    dueDate?: string;
    contractorName?: string;
    currency?: string;
    reference?: string;
  }): Promise<ApiResponse<{ item: ShoppingItem; payable: { id: number; reference: string; amount: number; status: string; dueDate: string } }> & { item: ShoppingItem; payable: { id: number; reference: string; amount: number; status: string; dueDate: string } }> => {
    const response = await api.post(`/shopping/${itemId}/create-payable`, data || {});
    return response.data;
  }
};
